import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { toApiRange } from '@/features/overview/overview.api';
import { VoucherReportTiles } from './voucher-report-tiles';

/**
 * Gift vouchers report — what was sold, what those vouchers paid for, and what
 * is still owed as future bookings.
 *
 * The same figures the Business Overview shows in its Money tab, for one date
 * range, with the sales split out. Vouchers carry no location, so there is no
 * location filter.
 */

function monthBounds() {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const last = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDay)}`;
  return { first, last };
}

export function VoucherReportPage() {
  const { t } = useTranslation();
  const [start, setStart] = useState(() => monthBounds().first);
  const [end, setEnd] = useState(() => monthBounds().last);

  // The tiles own the query; the filters lock while it is in flight.
  const fetching = useIsFetching({ queryKey: ['vouchers', 'report'] }) > 0;

  const range = useMemo(
    () => (start && end ? toApiRange(start, end) : null),
    [start, end]
  );

  const fields: FilterField[] = [
    {
      id: 'start',
      label: t('vouchers.report.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: setStart,
    },
    {
      id: 'end',
      label: t('vouchers.report.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: setEnd,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vouchers.report.title')}
        description={t('vouchers.report.subtitle')}
      />

      <FilterBar busy={fetching} fields={fields} />

      <div className="bg-muted/50 text-muted-foreground flex gap-2 rounded-lg border p-3 text-xs">
        <Info className="mt-0.5 size-4 shrink-0" />
        <p>{t('vouchers.report.basisNotice')}</p>
      </div>

      {range && (
        <VoucherReportTiles from={range.startDate} to={range.endDate} />
      )}
    </div>
  );
}
